import { useInfiniteQuery } from '@tanstack/react-query';
import { Interval } from '@ui/constants/poll';
import { TABS } from '@ui/features/Trade/constants';
import { useTabsStore } from '@ui/features/Trade/store/useTabsStore';
import { useDataChainId } from '@ui/hooks/datachain';
import { isNil } from 'lodash-es';
import { Nullable } from 'vitest';

import { GetHistoryRequest } from '../../generated';
import { useBackendPairsAll } from '../getPairs/useBackendPairsAll';
import { tradeHistoryQuery } from './query';
import { TradeHistoryResponse } from './type';

export type UseTradeHistoryInfiniteParam = {
  userAddress: Nullable<GetHistoryRequest['userAddress']>;
  initialParam: {
    page: number;
    size: number;
  };
};
export const useTradeHistoryInfinite = (
  params: UseTradeHistoryInfiniteParam,
) => {
  const chainId = useDataChainId();
  const currentTab = useTabsStore((s) => s.currentTab);
  const { data: pairs } = useBackendPairsAll();

  return useInfiniteQuery({
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    getNextPageParam: (lastPage: TradeHistoryResponse) => {
      if (isNil(lastPage.totalPages) || isNil(lastPage.page)) {
        return;
      }
      const page = lastPage.page;
      const hasNext = lastPage.totalPages > page + 1;
      if (hasNext) {
        return {
          page: page + 1,
          size: params.initialParam.size,
        };
      }
    },
    initialPageParam: params.initialParam,
    queryKey: [
      'useTradeHistoryInfinite',
      ...tradeHistoryQuery.getKey({
        ...params.initialParam,
        userAddress: params.userAddress,
        chainId,
      }),
      Object.keys(pairs ?? {}),
    ],
    queryFn: async ({ pageParam }) => {
      return tradeHistoryQuery.getFn({
        ...pageParam,
        userAddress: params.userAddress,
        chainId,
        pairs: Object.values(pairs ?? {}),
      })();
    },
    enabled: !!params.userAddress && !!pairs,
    refetchInterval:
      currentTab === TABS.History ? Interval.FAST : Interval.SLOW,
  });
};
